import type { Capitolo } from '../tipi.ts'

const capitolo: Capitolo = {
  id: 'agenda',
  titolo: 'Agenda della sede, orari e turni',
  gruppo: 'Il lavoro con le persone',
  permesso: ['agenda.leggi', 'sede.agenda', 'mediazione.propria'],
  aCheServe: 'L’agenda mostra in una pagina sola gli impegni di una sede: appuntamenti, mediazioni, turni, stanze '
    + 'prenotate, riunioni, attività e chiusure. «La mia agenda» mostra solo i tuoi, in tutte le sedi.',
  parti: [
    {
      titolo: 'Che cosa si vede nell’agenda',
      permesso: 'agenda.leggi',
      testo: [
        'Nel menu, «Agenda». In «Agenda» scegli la sede, oppure «La mia agenda». Per difetto si apre la tua sede '
          + 'principale. Vedi le sedi dei tuoi incarichi, e quelle dei progetti su cui hai un incarico.',
        'In «Si vedono» accendi e spegni gli strati: «Appuntamenti», «Mediazioni», «Turni», «Stanze prenotate», '
          + '«Riunioni», «Attività», «Chiusure». La scelta resta nell’indirizzo della pagina.',
        'Appuntamenti e mediazioni compaiono come «Appuntamento» e «Mediazione», con l’operatore o il mediatore e la '
          + 'stanza. Il nome della persona lo vede solo chi segue la sua scheda; il motivo dell’appuntamento compare '
          + 'solo aprendolo. Le riunioni visibili ai soli partecipanti, agli altri, compaiono come «Occupato» nella '
          + 'stanza.',
      ],
      immagine: {
        file: 'agenda-settimana.webp', larghezza: 1200, altezza: 800, daFare: true,
        descrizione: 'L’agenda di una sede nella vista Settimana, con turni in testa al giorno, appuntamenti e mediazioni.',
        cosaMostra: 'Agenda, sede Perugia, vista Settimana, utente COO con turni, appuntamenti, mediazioni e una riunione.',
      },
    },
    {
      titolo: 'Viste, colori e stampa',
      permesso: 'agenda.leggi',
      passi: [
        '«Giorno»: una colonna per stanza. In «Una colonna per» scegli «Persona in turno» per una colonna per chi è '
          + 'in turno; gli impegni senza stanza o persona stanno in «Senza stanza» o «Altri impegni».',
        '«Settimana»: dal lunedì al sabato. Le ore in cui la sede è chiusa sono ombreggiate; festività e chiusure '
          + 'hanno una fascia grigia. I turni stanno in testa al giorno.',
        '«Mese»: un clic sul giorno lo apre nella vista «Giorno».',
        '«Elenco»: gli impegni uno sotto l’altro; «Stampa» li stampa.',
        '‹ e › cambiano periodo, «Oggi» torna a oggi.',
      ],
      testo: [
        'Rosso: la stessa persona o la stessa stanza due volte nello stesso orario. Arancione: sede chiusa, fuori '
          + 'orario o persona che si è segnata non disponibile. Tratteggio: «Occupato», un impegno che non vedi.',
      ],
    },
    {
      titolo: 'Fissare una riunione, una prenotazione o un evento',
      permesso: 'agenda.scrivi',
      passi: [
        'Fai clic su uno spazio vuoto dell’agenda, oppure premi «Nuovo impegno»: giorno, ora e stanza (o persona) sono '
          + 'già compilati.',
        'In «Che cosa» scegli «Riunione», «Prenotazione di una stanza», «Attività» o «Evento». Per un impegno solo '
          + 'tuo, in «Sede» scegli «— nessuna: impegno personale —».',
        'Scrivi il «Titolo» (obbligatorio per riunioni ed eventi), il «Giorno», «Dalle» e «Alle», oppure spunta '
          + '«Tutto il giorno». Per la prenotazione scegli la «Stanza».',
        'In «Chi lo vede» scegli «Chi lavora nella sede» oppure «Solo i partecipanti (gli altri vedono la stanza '
          + 'occupata)».',
        'In «Invita un collega» scrivi nome o cognome e premi il collega. Riceve un avviso con giorno e sede, senza il '
          + 'titolo.',
        'Premi «Fissa».',
      ],
      nota: 'Non scrivere nel titolo e nelle note i nomi delle persone assistite: un impegno con una persona è un '
        + 'appuntamento della scheda o una mediazione.',
    },
    {
      titolo: 'Impegni che si ripetono',
      permesso: ['agenda.scrivi', 'agenda.turni'],
      passi: [
        'In «Si ripete» scegli «Ogni giorno», «Ogni settimana» (con i giorni), «Ogni mese, nello stesso giorno» o '
          + '«Ogni mese, nella stessa settimana (per esempio il secondo martedì)». «Ogni quante» a 2 vuol dire una '
          + 'volta sì e una no.',
        'In «Fino a» scegli «Un giorno» e l’«Ultimo giorno», oppure «Un numero di volte». Al più due anni o 200 volte.',
        '«Salta festività e chiusure della sede» toglie le volte che cadono in una festa nazionale o in una chiusura; '
          + 'contano comunque nel numero di volte.',
        'Per cambiare un giorno solo, apri l’impegno in quel giorno: «Annulla questa volta» lo toglie, «Ripristina '
          + 'questa volta» lo rimette com’era. «Annulla tutte le volte» annulla l’intero impegno.',
      ],
    },
    {
      titolo: 'I turni',
      permesso: 'agenda.turni',
      testo: [
        'I turni li programmano coordinatori, responsabili d’area e Direzione. In turno vanno il personale con un '
          + 'incarico sulla sede (o su tutta CIDIS, o su un progetto della sede) e i mediatori dell’albo che lavorano '
          + 'nella sede, anche se non usano il gestionale.',
      ],
      passi: [
        '«Nuovo impegno», in «Che cosa» scegli «Turno».',
        'In «Chi» scegli la persona fra «Personale della sede» e «Mediatori dell’albo», poi la «Funzione», per esempio '
          + '«Accoglienza» o «Sportello».',
        'Giorno, ore ed eventuale ripetizione come per gli altri impegni; premi «Fissa».',
      ],
      nota: 'La persona riceve l’avviso «Turno fissato nella sede …» e trova il turno nella sua agenda. Il mediatore '
        + 'senza utenza non riceve niente: avvisalo tu. I turni servono a organizzare il lavoro della sede, non a '
        + 'controllare le presenze.',
    },
    {
      titolo: 'Spostare turni e prenotazioni trascinandoli',
      permesso: ['agenda.scrivi', 'agenda.turni'],
      passi: [
        'Nel computer, trascina il turno o la prenotazione in un altro orario o in un altro giorno.',
        'Nel «Giorno» a colonne puoi trascinarlo in un’altra stanza, o con «Persona in turno» su un’altra persona.',
        'Un impegno che si ripete si sposta solo nello stesso giorno: il cambio vale per quel giorno. Su un’altra '
          + 'persona diventa il suo sostituto per quel giorno.',
      ],
      nota: 'Se la sede è chiusa o la persona non è disponibile compare un avviso: «Sposta lo stesso» oppure «Lascia '
        + 'com’era». Le riunioni non si trascinano: apri la riunione e premi «Modifica».',
    },
    {
      titolo: '«Segnati non disponibile»',
      permesso: ['agenda.leggi', 'mediazione.propria'],
      passi: [
        'Nell’agenda premi «Segnati non disponibile».',
        'Scegli «Dal» e, per un periodo, «Al». Per una parte del giorno spunta «Solo in certe ore».',
        'Premi «Segna».',
      ],
      testo: [
        'Non c’è un campo per il motivo e non va scritto da nessuna parte: risulti solo «non disponibile». Lo vede chi '
          + 'programma i turni della sede. Chi ti mette in turno, o ti programma una mediazione, in quei giorni riceve '
          + 'un avviso e può decidere «Programma lo stesso».',
        'Nella risposta di assenza della posta, la spunta «Segna anche nell’agenda» fa la stessa cosa per i giorni '
          + 'dell’assenza.',
      ],
    },
    {
      titolo: 'Appuntamenti con le persone',
      permesso: 'follow_up.gestisci',
      passi: [
        'Nella scheda, linguetta «Percorsi», riquadro «Ricontatti e appuntamenti», premi «Fissa un appuntamento».',
        'Compila «Giorno», «Ora», «Durata», «Sede» e, se la sede ne ha, la «Stanza». «Si ripete» e «Quanti in tutto» '
          + 'servono per gli appuntamenti fissi (al più 26).',
        'Scrivi il «Motivo»: resta nella scheda e non compare negli avvisi. Premi «Fissa l’appuntamento».',
        'Dopo l’appuntamento, «Registra l’intervento» apre l’intervento già compilato.',
      ],
      nota: 'Una stanza occupata blocca: scegli un’altra stanza o un altro orario. Due giorni prima arriva il promemoria '
        + '«Appuntamento il …», a chi lo segue.',
    },
    {
      titolo: 'Orari, chiusure e stanze della sede',
      permesso: ['sede.agenda', 'agenda.leggi'],
      testo: [
        'Nella pagina della sede, la linguetta «Orari e chiusure» ha l’«Orario settimanale», le «Chiusure e aperture '
          + 'straordinarie» e le festività nazionali dell’anno. La linguetta «Stanze» elenca stanze, sale e '
          + 'postazioni. Le leggono tutti; le modifica chi coordina la sede.',
      ],
      passi: [
        'Orario: per ogni giorno una o più fasce. «Vale solo in un periodo» serve per l’orario estivo; «Copia l’orario '
          + 'nell’anno dopo» lo ripete l’anno seguente.',
        'Chiusura: scegli il «Tipo» («Chiusura», «Ponte», «Festa patronale», «Apertura straordinaria»), i giorni e il '
          + '«Motivo». «Solo una parte del giorno» per una mezza giornata; «Ogni anno, negli stessi giorni» per il '
          + 'patrono.',
        'Stanza: «Nome», «Tipo», «Posti», le caratteristiche e «Si prenota dall’agenda». Una stanza non si cancella: '
          + 'si toglie la spunta «In uso».',
      ],
      nota: 'Una chiusura in un giorno con impegni avvisa chi li ha fissati e chi segue i ricontatti; l’avviso apre '
        + 'l’agenda di quel giorno. Non si annulla niente da solo.',
    },
    {
      titolo: '«Oggi in sede» nel cruscotto',
      permesso: 'agenda.leggi',
      testo: [
        'Il riquadro dice se la sede è aperta e con quale orario, chi è in turno e quanti appuntamenti, mediazioni e '
          + 'impegni ci sono, senza nomi. «Agenda del giorno» apre l’agenda.',
      ],
    },
    {
      titolo: 'Il promemoria del giorno dopo',
      permesso: ['agenda.leggi', 'mediazione.propria'],
      testo: [
        'Alle 16 arriva l’avviso con gli impegni del giorno dopo: turni, mediazioni, riunioni, prenotazioni ed '
          + 'eventi, con ora e sede. Il sabato arrivano anche quelli del lunedì. Gli avvisi non riportano nomi né '
          + 'titoli; l’avviso apre la tua agenda di quel giorno.',
      ],
    },
    {
      titolo: 'L’agenda in Outlook e sul telefono',
      permesso: ['agenda.leggi', 'mediazione.propria'],
      testo: [
        'Se CIDIS ha acceso il calendario in abbonamento, in «Il mio profilo», riquadro «Il calendario in Outlook e sul '
          + 'telefono», crei un collegamento a «La mia agenda» che Outlook, Google o il telefono rileggono da soli. I '
          + 'passi sono nel capitolo «Il gestionale sul telefono».',
      ],
    },
  ],
  avvertenze: [
    {
      quando: 'Nella stanza c’è già un altro impegno.',
      cosaFare: 'La stanza occupata blocca: scegli un’altra stanza o un altro orario.',
    },
    {
      quando: 'Avviso «la sede non è aperta».',
      cosaFare: 'L’impegno cade in un giorno di chiusura o fuori orario. Se va bene così, «Fissalo lo stesso».',
    },
    {
      quando: 'Avviso «… risulta non disponibile».',
      cosaFare: 'La persona si è segnata non disponibile in quel giorno. Scegli un’altra persona o un altro orario, '
        + 'oppure «Programma lo stesso».',
    },
    {
      quando: '«Un impegno che si ripete si sposta solo nello stesso giorno.»',
      cosaFare: 'Per cambiare i giorni apri l’impegno, premi «Modifica» e cambia la ripetizione.',
    },
    {
      quando: '«Quella sede non è fra quelle della tua agenda.»',
      cosaFare: 'L’avviso parlava di una sede fuori dai tuoi incarichi: vedi la tua sede di partenza. L’impegno lo '
        + 'trovi nella scheda della persona.',
    },
    {
      quando: 'Non trovi la voce «Agenda».',
      cosaFare: 'L’agenda la vede chi ha un incarico su una sede. Il mediatore ha «Il mio calendario».',
    },
  ],
  vediAnche: ['mediazioni', 'telefono', 'percorsi-interventi', 'cruscotto'],
  parole: ['calendario', 'turni', 'turno', 'stanze', 'sala', 'prenotazione', 'riunione', 'appuntamento', 'orario',
    'chiusura', 'festività', 'ferie', 'non disponibile', 'assenza', 'ics', 'outlook', 'promemoria', 'sostituto'],
}

export default capitolo
